import { useState, useEffect } from "react";
import { X, Route, Trash2, Zap, CheckCircle2, XCircle, Ruler, GitMerge, FileText } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError, API, openDownload } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";

const CONN_TYPES = ["ciemne włókno", "switch ↔ switch", "łącze firmowe"];

export default function ConnectionsPanel({ role, onClose, onShowPath, onRefresh }) {
  const { t: tr } = useI18n();
  const canEdit = role !== "odczyt";
  const [closures, setClosures] = useState([]);
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [path, setPath] = useState(null);
  const [busy, setBusy] = useState(false);
  const [name, setName] = useState("");
  const [connType, setConnType] = useState(CONN_TYPES[0]);
  const [conns, setConns] = useState([]);

  const loadConns = () => api.get("/connections").then(({ data }) => setConns(data)).catch(() => setConns([]));

  useEffect(() => {
    api.get("/closures").then(({ data }) => setClosures(data)).catch(() => setClosures([]));
    loadConns();
  }, []);

  const findPath = async () => {
    if (!from || !to) { toast.error(tr("Wybierz obie mufy")); return; }
    if (from === to) { toast.error(tr("Wybierz dwie różne mufy")); return; }
    setBusy(true); setPath(null);
    try {
      const { data } = await api.get("/connections/path", { params: { from_closure: from, to_closure: to } });
      setPath(data);
      if (data.feasible && data.path_coords?.length && onShowPath) onShowPath(data.path_coords);
      if (!data.feasible) toast.error(data.reason || tr("Brak trasy"));
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const createConn = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/connections", { from_closure: from, to_closure: to, name, conn_type: connType });
      toast.success(`${tr("Utworzono połączenie")} „${data.name}"`);
      setName(""); setPath(null);
      loadConns(); onRefresh && onRefresh();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const delConn = async (c) => {
    if (!window.confirm(`Usunąć połączenie „${c.name}" i zwolnić włókna?`)) return;
    try {
      await api.delete(`/connections/${c.id}`);
      toast.success(tr("Usunięto połączenie"));
      loadConns(); onRefresh && onRefresh();
    } catch (e) { toast.error(tr("Błąd usuwania")); }
  };

  const autoSplice = async (s) => {
    try {
      const { data } = await api.post(`/closures/${s.closure_id}/splice-auto`, { cable_a_id: s.in_cable_id, cable_b_id: s.out_cable_id });
      toast.success(`${tr("Utworzono spawów (1:1):")} ${data.created}`);
      findPath(); onRefresh && onRefresh();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const cname = (id) => closures.find((c) => c.id === id)?.number || "?";

  return (
    <div className="absolute inset-0 bg-slate-900 flex flex-col" data-testid="connections-panel">
      <div className="flex items-center justify-between px-4 py-3 border-b border-slate-700">
        <div className="flex items-center gap-2">
          <Route size={18} className="text-cyan-400" />
          <h2 className="font-heading font-bold text-base">{tr("Połączenia / Ciemne włókno")}</h2>
        </div>
        <button onClick={onClose} data-testid="connections-close" className="text-slate-400 hover:text-white"><X size={18} /></button>
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {/* Route finder */}
        <div className="bg-slate-800/60 border border-slate-700 rounded-lg p-3 space-y-2">
          <div className="text-xs font-semibold text-slate-300 uppercase tracking-wide">{tr("Wyznacz trasę między mufami")}</div>
          <div>
            <label className="text-xs text-slate-400 mb-1 block">{tr("Mufa źródłowa")}</label>
            <select value={from} onChange={(e) => setFrom(e.target.value)} data-testid="conn-from"
              className="w-full bg-slate-900 border border-slate-700 rounded px-2 py-2 text-sm outline-none focus:ring-1 focus:ring-cyan-500">
              <option value="">— wybierz —</option>
              {closures.map((c) => <option key={c.id} value={c.id}>{c.number}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs text-slate-400 mb-1 block">{tr("Mufa docelowa")}</label>
            <select value={to} onChange={(e) => setTo(e.target.value)} data-testid="conn-to"
              className="w-full bg-slate-900 border border-slate-700 rounded px-2 py-2 text-sm outline-none focus:ring-1 focus:ring-cyan-500">
              <option value="">— wybierz —</option>
              {closures.map((c) => <option key={c.id} value={c.id}>{c.number}</option>)}
            </select>
          </div>
          <button onClick={findPath} disabled={busy} data-testid="conn-find"
            className="w-full bg-blue-600 hover:bg-blue-500 disabled:opacity-50 rounded py-2 text-sm font-medium flex items-center justify-center gap-2">
            <Zap size={15} /> {tr("Wyznacz trasę")}
          </button>
        </div>

        {/* Result */}
        {path && (
          <div className="bg-slate-800/60 border border-slate-700 rounded-lg p-3 space-y-3" data-testid="conn-result">
            {path.feasible ? (
              <>
                <div className="flex items-center gap-2 text-green-400 text-sm font-semibold">
                  <CheckCircle2 size={16} /> {tr("Trasa możliwa")}
                </div>
                <div className="grid grid-cols-3 gap-2 text-center">
                  <div className="bg-slate-900 rounded p-2">
                    <div className="text-[11px] text-slate-400 flex items-center justify-center gap-1"><Ruler size={11} /> {tr("Długość")}</div>
                    <div className="font-mono text-cyan-300 text-sm">{path.total_length_m} m</div>
                  </div>
                  <div className="bg-slate-900 rounded p-2">
                    <div className="text-[11px] text-slate-400">{tr("Odcinki")}</div>
                    <div className="font-mono text-cyan-300 text-sm">{path.hops}</div>
                  </div>
                  <div className="bg-slate-900 rounded p-2">
                    <div className="text-[11px] text-slate-400">{tr("Wolne włókna")}</div>
                    <div className={`font-mono text-sm ${path.capacity_free_fibers > 0 ? "text-green-400" : "text-red-400"}`}>{path.capacity_free_fibers}</div>
                  </div>
                </div>

                <div>
                  <div className="text-xs font-semibold text-slate-300 mb-1">{tr("Mufy na trasie")}</div>
                  <div className="flex flex-wrap items-center gap-1 text-xs">
                    {path.closures.map((c, i) => (
                      <span key={c.id} className="flex items-center gap-1">
                        <span className="bg-purple-900/50 border border-purple-700 rounded px-1.5 py-0.5 text-purple-200">{c.number}</span>
                        {i < path.closures.length - 1 && <span className="text-slate-500">→</span>}
                      </span>
                    ))}
                  </div>
                </div>

                <div>
                  <div className="text-xs font-semibold text-slate-300 mb-1">{tr("Kable")}</div>
                  <div className="space-y-1">
                    {path.cables.map((c) => (
                      <div key={c.id} className="flex items-center justify-between text-xs bg-slate-900 rounded px-2 py-1">
                        <span className="text-blue-300">{c.number}</span>
                        <span className="text-slate-400">{c.length_m} m · wolne {c.free_count}/{c.fiber_count}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {path.schematic.length > 0 && (
                  <div>
                    <div className="text-xs font-semibold text-slate-300 mb-1 flex items-center gap-1"><GitMerge size={12} /> {tr("Schemat spawów w mufach")}</div>
                    <div className="space-y-1">
                      {path.schematic.map((s) => (
                        <div key={s.closure_id} className="text-xs bg-slate-900 rounded px-2 py-1.5 space-y-1" data-testid={`conn-schematic-${s.closure_id}`}>
                          <div className="flex items-center gap-2">
                            <span className="text-purple-300 font-medium">{s.closure_number}:</span>
                            <span className="text-blue-300">{s.in_cable}</span>
                            <span className="text-slate-500">⇄ spaw ⇄</span>
                            <span className="text-blue-300">{s.out_cable}</span>
                          </div>
                          <div className="flex items-center justify-between gap-2">
                            <div className="flex flex-col">
                              <span className="text-amber-300">{tr("Wolne włókna 1:1")}: <b>{s.suggested_count}</b>
                                {s.suggested_fibers?.length ? <span className="text-slate-400"> ({s.suggested_fibers.slice(0, 8).join(", ")}{s.suggested_fibers.length > 8 ? "…" : ""})</span> : null}</span>
                              {s.pairable > s.suggested_count && (
                                <span className="text-emerald-300/90 text-[11px]">{tr("Można też zespawać dowolne wolne↔wolne")}: <b>{s.pairable}</b> {tr("par")} ({s.free_in}↔{s.free_out} {tr("wolnych")})</span>
                              )}
                              {s.pairable === 0 && (
                                <span className="text-red-300 text-[11px]">{tr("Brak wolnych włókien na tym odcinku — dodaj lub zwolnij włókno")}</span>
                              )}
                            </div>
                            {canEdit && s.suggested_count > 0 && (
                              <button onClick={() => autoSplice(s)} data-testid={`conn-autosplice-${s.closure_id}`}
                                className="shrink-0 bg-emerald-700 hover:bg-emerald-600 text-white rounded px-2 py-0.5 text-[11px]">{tr("Auto-spaw")}</button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {canEdit && path.capacity_free_fibers > 0 && (
                  <div className="pt-2 border-t border-slate-700 space-y-2">
                    <input value={name} onChange={(e) => setName(e.target.value)} placeholder={tr("Nazwa połączenia (np. Firma XYZ)")}
                      data-testid="conn-name"
                      className="w-full bg-slate-900 border border-slate-700 rounded px-2 py-2 text-sm outline-none focus:ring-1 focus:ring-cyan-500" />
                    <select value={connType} onChange={(e) => setConnType(e.target.value)} data-testid="conn-type"
                      className="w-full bg-slate-900 border border-slate-700 rounded px-2 py-2 text-sm outline-none focus:ring-1 focus:ring-cyan-500">
                      {CONN_TYPES.map((ct) => <option key={ct} value={ct}>{tr(ct)}</option>)}
                    </select>
                    <button onClick={createConn} disabled={busy} data-testid="conn-create"
                      className="w-full bg-green-600 hover:bg-green-500 disabled:opacity-50 rounded py-2 text-sm font-medium">
                      {tr("Utwórz połączenie (zarezerwuj włókno + auto-spawy)")}
                    </button>
                  </div>
                )}
              </>
            ) : (
              <div className="flex items-center gap-2 text-red-400 text-sm">
                <XCircle size={16} /> {path.reason || tr("Brak trasy między mufami")}
              </div>
            )}
          </div>
        )}

        {/* Existing connections */}
        <div>
          <div className="text-xs font-semibold text-slate-300 uppercase tracking-wide mb-2">{tr("Utworzone połączenia")} ({conns.length})</div>
          {conns.length === 0 && <div className="text-slate-500 text-sm text-center py-4">{tr("Brak połączeń")}</div>}
          <div className="space-y-1.5">
            {conns.map((c) => (
              <div key={c.id} className="bg-slate-800/60 border border-slate-700 rounded px-3 py-2 flex items-center justify-between gap-2" data-testid="conn-row">
                <div className="min-w-0">
                  <div className="text-sm font-medium truncate">{c.name}</div>
                  <div className="text-xs text-slate-400">
                    <span className="text-cyan-400">{c.conn_type}</span> · {cname(c.from_closure)} → {cname(c.to_closure)} · {c.total_length_m} m · {c.hops} odc.
                  </div>
                </div>
                {canEdit && (
                  <div className="flex items-center gap-1 shrink-0">
                    <button onClick={() => openDownload(`/connections/${c.id}/document.pdf`)}
                      title={tr("Pobierz metrykę łącza (PDF)")} data-testid={`conn-pdf-${c.id}`}
                      className="p-1.5 rounded hover:bg-cyan-600/30 text-cyan-400"><FileText size={14} /></button>
                    <button onClick={() => delConn(c)} title={tr("Usuń połączenie")} data-testid={`conn-delete-${c.id}`}
                      className="p-1.5 rounded hover:bg-red-600/30 text-red-400"><Trash2 size={14} /></button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
